import { AfterViewChecked, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Constants } from 'src/shared/utilities/constants.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterService } from 'src/shared/services/cost-center.service';
import { EntityAdminConfig } from 'src/shared/Baseclasses/entity-admin.component';
import { Facility } from 'src/shared/models/facility.model';
import { PageLayoutService } from 'src/shared/services/page-layout.service';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { Subscription } from 'rxjs';
import { mergeMap, map, } from 'rxjs/operators';
import { UserService } from 'src/shared/services/user.service';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { User } from 'src/shared/models/user.model';

@Component({
    selector: 'smt-cost-center',
    templateUrl: './cost-center.component.html',
    styleUrls: ['./cost-center.component.scss']
})
export class CostCenterComponent extends EntityAdminConfig implements AfterViewChecked, OnDestroy, OnInit {
    @ViewChild('costCenterEditForm') public costCenterEditForm: NgForm;

    private readonly detailsTabName = "details";
    private readonly generalTabName = "general";
    private readonly staffingGridTabName = "staffingGrid"

    previousSelection: CostCenter;
    costCenters: CostCenter[] = [];
    schedules: ShiftSchedule[] = [];
    costCenterSlots: number;
    unsavedFunction: Function;
    isFormValidSubscription: Subscription;
    errorSubscription: Subscription;
    warningSubscription: Subscription;
    isFormDirtySubscription: Subscription;
    currentUser: User;

    private _valid = false;
    private _dirty = false;

    // Tabs
    generalTabActive = true;
    detailsTabActive = false;
    staffingGridTabActive = false;
    confirmUnsavedModalActive = false;
    confirmZeroCostCenterSlots = false;
    staffingTabActive = false;
    private scheduleSubscription: Subscription;
    selectedSubscription: Subscription;
    private reloadAdminListSubscription: Subscription;
    hasPermission = true;
    isSaveButtonEnabled: boolean;
    private validatingSubscription: Subscription;
    private isValidating: boolean;
    isFutureShiftRecordAvailable: boolean;
    confirmCopyModalActive = false;

    constructor(
        private _costCenterService: CostCenterService,
        private _costCenterConfigurationService: CostCenterConfigurationService,
        _pageLayout: PageLayoutService,
        private _userService: UserService,
        private _shiftScheduleService: ShiftScheduleService) {
        super(_pageLayout);

        this.scheduleSubscription = this._shiftScheduleService.schedules$.subscribe((schedules) => {
            this.schedules = schedules;
        });

        this.validatingSubscription = this._costCenterConfigurationService.isValidating$.subscribe(isValidating => {
            this.isValidating = isValidating;
        });
        this._costCenterConfigurationService.isFutureShiftRecordPresent$.subscribe(futureShiftRecordFlag => {
            this.isFutureShiftRecordAvailable = futureShiftRecordFlag;
            if (!this.isFutureShiftRecordAvailable) {
                this._costCenterConfigurationService.clearWarning();
            }
        });


    }

    ngAfterViewChecked() {
        let enableAdminActionLoading = !this.initialLoading &&
            (this._costCenterConfigurationService.isSaving ||
                this._costCenterConfigurationService.isAdminActionLoading);
        let enableAdminListLoading = !this.initialLoading && this._costCenterConfigurationService.isAdminListLoading;

        if (enableAdminActionLoading === this.adminActionLoading && enableAdminListLoading === this.adminListLoading) {
            return;
        }

        setTimeout(() => {
            this.setLoading([this.adminActionLoadingSection], enableAdminActionLoading);
            this.setLoading([this.adminListLoadingSection], enableAdminListLoading);
        });
    }

    ngOnDestroy() {
        super.cleanUp();

        if (this.reloadAdminListSubscription) {
            this.reloadAdminListSubscription.unsubscribe();
        }

        if (this.scheduleSubscription) {
            this.scheduleSubscription.unsubscribe();
        }

        if (this.validatingSubscription) {
            this.validatingSubscription.unsubscribe();
        }
    }

    async ngOnInit() {
        super.initialize(
            Constants.CostCenter,
            ["Name", "Facility Name", "Facility Acronym", "Last Modified"],
            async () => this._costCenterConfigurationService.setSelected(this.selection),
            async (mostRecent: boolean) => this.refreshCostCenterList(mostRecent),
            async () => this.appendCostCenterList(),
            async () => this._costCenterConfigurationService.announceSave(),
            () => this.setCurrentSelectionLabel(),
            () => { return "Remove"; },
            () => { return "Copy"; });

        this.isFormValidSubscription = this._costCenterConfigurationService.isFormValid$
            .pipe(
                map(isFormValid => { this._valid = isFormValid; })
            )
            .subscribe();

        this.isFormDirtySubscription = this._costCenterConfigurationService.isFormDirty$.subscribe((isFormDirty) => {
            if (!isFormDirty) {
                this.markFormClean();
            } else {
                this.markFormDirty();
            }
        });

        this.errorSubscription = this._costCenterConfigurationService.error$.subscribe((errorMessage) => {
            this.setErrorMessage(errorMessage);
        });

        this.warningSubscription = this._costCenterConfigurationService.warning$.subscribe((warningMessage) => {
            this.setWarningMessage(warningMessage);
        });

        this.selectedSubscription = this._costCenterConfigurationService.selected$.subscribe((selection) => {
            this.selection = selection;

            if (selection && (!this.previousSelection || this.previousSelection.id != selection.id)) {
                this.setTabStatus(this.generalTabName);
            }

            this._costCenterConfigurationService.clearWarning();
            this.setCurrentSelectionLabel();

            this.previousSelection = selection;
        });

        this.reloadAdminListSubscription = this._costCenterConfigurationService.reloadAdminList$.subscribe((mostRecent) => {
            super.refreshAdminList(mostRecent, true);
        })

        this.refreshClientCostCenterLimit();
        
        await this._userService.GetCurrentUser().then((user) => {
            this.currentUser = user;
            if (this.currentUser.applicationRoleName == Constants.ShiftLeaderWithoutReportAccess || this.currentUser.applicationRoleName == Constants.ShiftLeaderWithReportAccess) {
                this.hasPermission = false;
            }
        });

    }

    async addNewCostCenter() {
        await this.refreshClientCostCenterLimit();
        if (this.costCenterSlots > 0) {
            this._costCenterConfigurationService.checkAndAnnounceFutureShiftRecordPresence(0);
            this.addCostCenter();
        }
        else {
            this.confirmZeroCostCenterSlots = true;
        }
    }

    async onSaveClick() {
        await this.refreshClientCostCenterLimit();
        this.isFutureShiftRecordAvailable = false;
        if (this.costCenterSlots <= 0) {
            this.confirmZeroCostCenterSlots = true;
        }
    }

    addCostCenter() {
        this.setActiveTab(this.generalTabName);
        this.selection = new CostCenter();
        this.selection.id = 0;
        this.selection.inactiveDate = Date.now();
        this.selection.facility = new Facility();
        this.showAdminActionWindow = true;
        this._costCenterConfigurationService.setSelected(this.selection);

        super.limitAdminActionLabel();
    }

    async appendCostCenterList(): Promise<void> {
        let result = await this._costCenterService.getCostCenters(
            false,
            this.offset,
            this.itemsPerPage,
            this.searchText,
            this.selectedFilter,
            this.getSortOrderString(),
            false);

        this.costCenters = this.costCenters.concat(result);
    }

    async refreshClientCostCenterLimit() {
        this.costCenterSlots = await this._costCenterService.getCostCentersRemaining();
    }

    get shouldDisplayStaffingTab(): boolean {
        return this.selection &&
            !!this.selection.id &&
            this.selection.costCenterType &&
            (
                (this.selection.ancillaryConfiguration && this.selection.ancillaryConfiguration.id) ||
                (this.selection.inpatientConfiguration && this.selection.inpatientConfiguration.id)
            );
    }

    get isFormDirty(): boolean {
        return this._dirty || (this.costCenterEditForm && this.costCenterEditForm.dirty);
    }

    get isFormValid(): boolean {
        return this._valid && (this.costCenterEditForm && this.costCenterEditForm.valid);
    }

    get disableStaffinGrid(): boolean {
        if (this.isFutureShiftRecordAvailable) {
            return this.isFutureShiftRecordAvailable;
        }
        else {
            return !this.hasPermission;
        }
    }

    markFormClean() {
        if (!this.costCenterEditForm) {
            return;
        }

        Object.keys(this.costCenterEditForm.controls).forEach(control => {
            this.costCenterEditForm.controls[control].markAsPristine();
        });

        this.costCenterEditForm.form.markAsPristine();
        this._dirty = false;
        this._costCenterConfigurationService.clearWarning();
    }

    markFormDirty() {
        this._dirty = true;
    }

    onAddCostCenterClick() {
        this.warnIfDirty(() => {
            this.addNewCostCenter();
        });
    }

    onCancel() {
        this.warnIfDirty(() => {
            super.onCancel();
            this.markFormClean();
        });
    }

    async onSearchKey(event) {
        this.warnIfDirty(() => {
            super.onSearchKey(event);
        });
    }
    onSortTypesDropDownChanged(event) {
        this.warnIfDirty(() => {
            super.onSortTypesDropDownChanged(event);
        });
    }

    onChangeSortOrder() {
        this.warnIfDirty(() => {
            super.onChangeSortOrder();
        });
    }

    async onRemoveCostCenterClick() {
        if (!this.selection) {
            return;
        }

        let result = await this._costCenterService.removeCostCenter(this.selection.id);

        this.confirmModalActive = false;

        if (result) {
            super.refreshAdminList(false, false);
        }
        else {
            this.setErrorMessage("Cost center delete failed.");
        }
    }

    onSelection(costCenter: CostCenter) {
        if (costCenter && this.selection && costCenter.id == this.selection.id) {
            return;
        }

        this.warnIfDirty(() => {
            this._costCenterConfigurationService.announceFormIsDirty(false);
            super.onSelection(costCenter);
        });
    }
    
    refreshCurrentSelection(costCenterId: number) {
        let costCenter = this.costCenters.find(n => n.id == costCenterId);
        this.warnIfDirty(() => {
            this._costCenterConfigurationService.announceFormIsDirty(false);
            super.onSelection(costCenter);
        });
    }

    async refreshCostCenterList(mostRecent: boolean, skipFormClean: boolean = false): Promise<CostCenter[]> {
        await this.refreshClientCostCenterLimit();

        this._costCenterConfigurationService.announceAdminListLoading(true);

        let result = await this._costCenterService.getCostCenters(
            mostRecent,
            this.offset,
            this.itemsPerPage,
            this.searchText,
            this.selectedFilter,
            this.getSortOrderString(),
            false);

        this.costCenters = result;
        if (!skipFormClean) {
            this.markFormClean();
        }

        this._costCenterConfigurationService.announceAdminListLoading(false);

        return result;
    }

    setActiveTab(tabType: string) {
        this.warnIfDirty(() => {
            this.setTabStatus(tabType);
        });


    }

    setTabStatus(tabType: string) {
        switch (tabType) {
            case this.generalTabName: {
                this.generalTabActive = true;
                this.detailsTabActive = false;
                this.staffingGridTabActive = false;
                break;
            }
            case this.detailsTabName: {
                this.generalTabActive = false;
                this.detailsTabActive = true;
                this.staffingGridTabActive = false;
                break;
            }
            case this.staffingGridTabName: {
                this.generalTabActive = false;
                this.detailsTabActive = false;
                this.staffingGridTabActive = true;
                break;
            }
        }
        if(this.selection)
        this._costCenterConfigurationService.checkAndAnnounceFutureShiftRecordPresence(this.selection.id);

        this._costCenterConfigurationService.setActiveTab(tabType);
    }

    warnIfDirty(unsavedFunction: () => void) {
        if (this.isFormDirty) {
            this.confirmUnsavedModalActive = true;
            this.unsavedFunction = () => {
                unsavedFunction();
                this.markFormClean();
                this.confirmUnsavedModalActive = false;
                this.refreshCostCenterList(false, true).then(costcenters => {
                    this.refreshCurrentSelection(this.selection.id);
                });
            };
        }
        else {
            unsavedFunction();
            this.markFormClean();
            this.confirmUnsavedModalActive = false;
        }
    }

    setCurrentSelectionLabel(): string {
        if (!this.selection) {
            return "";
        }

        this.adminActionLabelText = this.selection.name || "New Cost Center";

        return this.adminActionLabelText;
    }

    enableSaveButton(): boolean {
        if (this._costCenterConfigurationService.isSaving || !this.hasPermission || this.isValidating || !this.isFormDirty) {
            return false;
        }

        // Form validation is different for the staffing grid tab and does not use the ngform validation.
        // Other tabs will check if the form is valid.
        if (this.staffingGridTabActive) {
            return this._costCenterConfigurationService.enableSaveOnStaffingGrid(this.selection, this.schedules.filter(s => !s.inactiveDate));
        }

        return this.isFormValid;
    }

    async onCopyCostCenter() {
        if (!this.selection) {
            return;
        }
        this.initialLoading = true;
        let copiedCostCenter: any = await this._costCenterService.copyCostCenter(this.selection.id);
        if (copiedCostCenter.id > 0) {
            this.selection = copiedCostCenter;
            await this.refreshCostCenterList(true);
            super.refreshAdminList(true,true);
            this.confirmCopyModalActive = true;
            this._costCenterConfigurationService.setSelected(this.selection);
            this._costCenterConfigurationService.setActiveTab(this.generalTabName);
        }
        this.initialLoading = false;
    }
}
